import { useCallback, useEffect, useRef, useState } from "react";
import api, { installationMessage } from "services/installation-api";
import { checkedTestResult, TEST_LIMIT_MS, TEST_TERMINAL } from "./deviceTest";

// Optional installation device test: polls every five seconds, reads at once
// on a matching Socket.IO hint, and stops waiting after at most three
// minutes. Nothing starts until start() is called. It never disconnects the
// app's shared socket, and unmounting only cleans up local work (the panel
// decides whether to ask the server to cancel).
export default function useInstallationDeviceTest({ session, deviceId, socket }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const active = useRef(null);
  const lastTestId = useRef(null);
  const generation = useRef(0);
  const request = useRef(null);
  const reading = useRef(false);

  const stopLocal = useCallback(() => {
    generation.current += 1;
    request.current?.abort();
    request.current = null;
    active.current = null;
    reading.current = false;
  }, []);

  useEffect(() => {
    stopLocal();
    lastTestId.current = null;
    setResult(null);
    setError("");
    setRemaining(0);
    setStarting(false);
    return stopLocal;
  }, [session?.session_id, deviceId, stopLocal]);

  const refresh = useCallback(async () => {
    const job = active.current;
    if (!job || reading.current) return;
    reading.current = true;
    const version = generation.current;
    const controller = new AbortController();
    request.current = controller;
    try {
      const next = checkedTestResult(
        await api.deviceTest(session, job.testId, controller.signal),
        deviceId, job.testId,
      );
      if (version !== generation.current) return;
      setResult(next);
      setError("");
      if (TEST_TERMINAL.includes(next.status)) {
        active.current = null;
        setRemaining(0);
      }
    } catch (problem) {
      if (version === generation.current && !controller.signal.aborted) {
        setError(`Could not check the device test: ${installationMessage(problem)}`);
      }
    } finally {
      if (version === generation.current) reading.current = false;
    }
  }, [session, deviceId]);

  const start = useCallback(async (draft) => {
    stopLocal();
    const version = generation.current;
    const deadline = performance.now() + TEST_LIMIT_MS;
    const controller = new AbortController();
    request.current = controller;
    setStarting(true);
    setError("");
    setResult(null);
    setRemaining(Math.round(TEST_LIMIT_MS / 1000));
    // Bounds even a hung POST; the server also expires its own test.
    const timeout = setTimeout(() => controller.abort(), TEST_LIMIT_MS);
    try {
      const next = checkedTestResult(
        await api.startDeviceTest(session, draft, controller.signal), deviceId,
      );
      if (version !== generation.current) return;
      lastTestId.current = next.test_id;
      const serverBudget = Math.max(0, Date.parse(next.expires_at) - Date.parse(next.server_now));
      active.current = TEST_TERMINAL.includes(next.status) ? null : {
        testId: next.test_id,
        deadline: Math.min(deadline, performance.now() + serverBudget),
      };
      setResult(next);
      setRemaining(active.current
        ? Math.max(0, Math.ceil((active.current.deadline - performance.now()) / 1000))
        : 0);
    } catch (problem) {
      if (version === generation.current) {
        setError(controller.signal.aborted
          ? "Starting the test timed out. No verified reading is available."
          : installationMessage(problem));
        setRemaining(0);
      }
    } finally {
      clearTimeout(timeout);
      if (version === generation.current) setStarting(false);
    }
  }, [session, deviceId, stopLocal]);

  useEffect(() => {
    const timer = setInterval(() => {
      const job = active.current;
      if (!job) return;
      const seconds = Math.max(0, Math.ceil((job.deadline - performance.now()) / 1000));
      setRemaining(seconds);
      if (seconds === 0) {
        stopLocal();
        setResult((previous) => (previous ? { ...previous, status: "timed_out" } : previous));
        setError("No verified fresh report was retrieved within 3 minutes. The device is not necessarily offline.");
      }
    }, 250);
    const poll = setInterval(refresh, 5000);
    return () => {
      clearInterval(timer);
      clearInterval(poll);
    };
  }, [refresh, stopLocal]);

  useEffect(() => {
    if (!socket) return undefined;
    const onUpdate = (hint) => {
      if (hint?.test_id && hint.test_id === active.current?.testId) refresh();
    };
    const onConnect = () => refresh();
    socket.on("installationDeviceTestUpdated", onUpdate);
    socket.on("connect", onConnect);
    return () => {
      socket.off("installationDeviceTestUpdated", onUpdate);
      socket.off("connect", onConnect);
    };
  }, [socket, refresh]);

  const cancel = useCallback(async () => {
    const job = active.current;
    stopLocal();
    const version = generation.current;
    setRemaining(0);
    setResult((previous) => (previous && previous.status === "waiting" ? { ...previous, status: "cancelled" } : previous));
    if (!job) return;
    const controller = new AbortController();
    request.current = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      checkedTestResult(await api.cancelDeviceTest(session, job.testId, controller.signal), deviceId, job.testId);
      if (version === generation.current) setError("");
    } catch (problem) {
      if (version === generation.current) {
        setError(`Stopped listening locally; server cancellation was not confirmed. ${installationMessage(problem)}`);
      }
    } finally {
      clearTimeout(timeout);
    }
  }, [session, deviceId, stopLocal]);

  // After a local timeout, read the server's stored result once. A report
  // ingested before the server deadline can still be returned; this never
  // restarts or extends the waiting window.
  const checkStored = useCallback(async () => {
    const testId = lastTestId.current;
    if (!testId || active.current) return;
    const version = generation.current;
    const controller = new AbortController();
    request.current = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    setChecking(true);
    try {
      const next = checkedTestResult(await api.deviceTest(session, testId, controller.signal), deviceId, testId);
      if (version !== generation.current) return;
      // A server that still says "waiting" after our local deadline is shown
      // as timed out locally; we do not resume listening.
      setResult(next.status === "waiting" ? { ...next, status: "timed_out" } : next);
      setError(next.status === "received" ? "" : "The server has no qualifying report for this test.");
    } catch (problem) {
      if (version === generation.current) {
        setError(controller.signal.aborted
          ? "Checking the stored result timed out."
          : `Could not check the stored test result: ${installationMessage(problem)}`);
      }
    } finally {
      clearTimeout(timeout);
      setChecking(false);
    }
  }, [session, deviceId]);

  const waiting = Boolean(result?.status === "waiting" && remaining > 0);
  return { result, error, remaining, starting, checking, waiting, start, cancel, checkStored };
}
